import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import TimersPanel from '../components/TimersPanel';
import {
  ActionBar,
  Badge,
  Button,
  FormGrid,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageActions,
  PageHeader,
  PageHeaderMain,
  Select,
  Subtitle,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  TabBar,
  TabBtn,
  Table,
  TableWrap,
  TextArea,
  Title,
  Toast,
  Toolbar,
} from '../components/ui';

interface CommandItem {
  _id: string;
  trigger: string;
  description: string;
  response: string;
  action: string;
  cooldownSeconds: number;
  adminOnly: boolean;
  active: boolean;
}

const emptyForm = {
  trigger: '',
  description: '',
  response: '',
  action: 'static',
  cooldownSeconds: 5,
  adminOnly: false,
  active: true,
};

const ACTION_LABELS: Record<string, string> = {
  static: 'Respuesta estática',
  balance: 'Genialitos',
  cards: 'Cartas',
  inventory: 'Inventario',
  ranking: 'Top Genialitos',
  admin_give_card: 'Admin dar carta',
  admin_remove_card: 'Admin quitar carta',
  admin_add_balance: 'Admin sumar Genialitos',
  admin_remove_balance: 'Admin quitar Genialitos',
  oracle_self: 'Oráculos (míos)',
  oracle_check: 'Oráculos (nick opcional)',
  oracle_ranking: 'Top oráculos',
  admin_give_oracle: 'Admin dar oráculos',
  admin_remove_oracle: 'Admin quitar oráculos',
  oracle_request: 'Pedir oráculo (cola)',
};

/** Glosario breve de cada placeholder editable en la plantilla */
const PLACEHOLDER_GLOSSARY: Record<string, string> = {
  '{displayName}': 'nombre visible de quien escribe',
  '{username}': 'login Twitch de quien escribe',
  '{target}': 'usuario objetivo del comando',
  '{amount}': 'cantidad numérica del comando',
  '{genialitos}': 'saldo de genialitos',
  '{balance}': 'igual que genialitos',
  '{ranking}': 'texto del top genialitos',
  '{oracleClaims}': 'oráculos disponibles',
  '{oracleUsed}': 'claims usados este directo',
  '{oracleMax}': 'máx. claims por directo (3)',
  '{note}': 'texto del pedido (opcional)',
  '{oracleRanking}': 'texto del top oráculos',
  '{cards}': 'lista de cartas del usuario',
  '{discord}': 'link Discord (config bot)',
  '{channel}': 'canal Twitch del stream',
};

/** Placeholders útiles por acción (siempre editables en la plantilla) */
const ACTION_PLACEHOLDERS: Record<string, string[]> = {
  static: ['{displayName}', '{username}', '{genialitos}', '{oracleClaims}', '{discord}', '{channel}'],
  balance: ['{displayName}', '{username}', '{genialitos}', '{balance}'],
  cards: ['{displayName}', '{username}', '{cards}', '{genialitos}'],
  inventory: ['{displayName}', '{username}', '{cards}', '{genialitos}'],
  ranking: ['{ranking}', '{displayName}'],
  admin_give_card: ['{target}', '{amount}', '{displayName}', '{username}'],
  admin_remove_card: ['{target}', '{amount}', '{displayName}', '{username}'],
  admin_add_balance: ['{target}', '{amount}', '{genialitos}', '{balance}', '{displayName}'],
  admin_remove_balance: ['{target}', '{amount}', '{genialitos}', '{balance}', '{displayName}'],
  oracle_self: ['{displayName}', '{username}', '{oracleClaims}', '{oracleUsed}', '{oracleMax}'],
  oracle_check: ['{target}', '{oracleClaims}', '{oracleUsed}', '{oracleMax}', '{displayName}'],
  oracle_ranking: ['{oracleRanking}', '{ranking}', '{displayName}'],
  admin_give_oracle: ['{target}', '{amount}', '{oracleClaims}', '{displayName}'],
  admin_remove_oracle: ['{target}', '{amount}', '{oracleClaims}', '{displayName}'],
  oracle_request: ['{displayName}', '{amount}', '{note}', '{oracleClaims}', '{oracleUsed}', '{oracleMax}'],
};

const RESPONSE_PLACEHOLDERS: Record<string, string> = {
  static: 'Ej: Unite al Discord: {discord}',
  balance: 'Ej: @{displayName}, tenés {genialitos} Genialitos.',
  cards: 'Ej: @{displayName} | Cartas: {cards}',
  inventory: 'Ej: @{displayName} | Inventario: {cards}',
  ranking: 'Ej: Top Genialitos: {ranking}',
  admin_give_card: 'Opcional. Placeholders: {username}, {target}, {amount}',
  admin_remove_card: 'Opcional. Placeholders: {username}, {target}, {amount}',
  admin_add_balance: 'Opcional. Ej: @{target} +{amount} Genialitos. Total: {genialitos}',
  admin_remove_balance: 'Opcional. Ej: @{target} -{amount} Genialitos. Total: {genialitos}',
  oracle_self: 'Ej: @{displayName}, tenés {oracleClaims} oráculo(s).',
  oracle_check: 'Ej: @{target} tiene {oracleClaims} oráculo(s).',
  oracle_ranking: 'Ej: Top oráculos: {oracleRanking}',
  admin_give_oracle: 'Ej: @{target} +{amount} oráculo(s). Total: {oracleClaims}.',
  admin_remove_oracle: 'Ej: @{target} -{amount} oráculo(s). Total: {oracleClaims}.',
  oracle_request: 'Ej: @{displayName}, pediste {amount} oráculo(s). {note}',
};

const DEFAULT_TEMPLATES: Record<string, string> = {
  balance: '@{displayName}, tenés {genialitos} Genialitos.',
  cards: '@{displayName} | Cartas: {cards}',
  inventory: '@{displayName} | Inventario: {cards}',
  ranking: 'Top Genialitos: {ranking}',
  admin_give_card: '@{target} recibió {amount} carta(s).',
  admin_remove_card: 'Se quitó carta a @{target}.',
  admin_add_balance: '@{target} +{amount} Genialitos. Total: {genialitos}.',
  admin_remove_balance: '@{target} -{amount} Genialitos. Total: {genialitos}.',
  oracle_self: '@{displayName}, tenés {oracleClaims} oráculo(s) disponible(s).',
  oracle_check: '@{target} tiene {oracleClaims} oráculo(s) disponible(s).',
  oracle_ranking: 'Top oráculos: {oracleRanking}',
  admin_give_oracle: '@{target} +{amount} oráculo(s). Total: {oracleClaims}.',
  admin_remove_oracle: '@{target} -{amount} oráculo(s). Total: {oracleClaims}.',
  oracle_request: '@{displayName}, pediste {amount} oráculo(s). Queda en cola de entrega.',
};

const CommandsPage: React.FC = () => {
  const [tab, setTab] = useState<'commands' | 'timers'>('commands');
  const [commands, setCommands] = useState<CommandItem[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');

  const load = async () => {
    const { data } = await api.get('/commands');
    setCommands(data);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) =>
      c.trigger.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q) ||
      c.action.toLowerCase().includes(q) ||
      (ACTION_LABELS[c.action] || '').toLowerCase().includes(q) ||
      c.response.toLowerCase().includes(q),
    );
  }, [commands, search]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (cmd: CommandItem) => {
    setEditingId(cmd._id);
    setForm({
      trigger: cmd.trigger,
      description: cmd.description,
      response: cmd.response || DEFAULT_TEMPLATES[cmd.action] || '',
      action: cmd.action,
      cooldownSeconds: cmd.cooldownSeconds,
      adminOnly: cmd.adminOnly,
      active: cmd.active,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      await api.put(`/commands/${editingId}`, form);
      setToast('Comando actualizado');
    } else {
      await api.post('/commands', form);
      setToast('Comando creado');
    }
    closeModal();
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('¿Eliminar este comando?')) return;
    await api.delete(`/commands/${id}`);
    setToast('Comando eliminado');
    load();
  };

  const toggle = async (id: string) => {
    await api.patch(`/commands/${id}/toggle`);
    load();
  };

  const responseHint = RESPONSE_PLACEHOLDERS[form.action] || 'Variables: {username}, {displayName}, {genialitos}, {ranking}, {cards}, {discord}';
  const glossaryKeys = ACTION_PLACEHOLDERS[form.action] || Object.keys(PLACEHOLDER_GLOSSARY);

  const onActionChange = (nextAction: string) => {
    const prevDefault = DEFAULT_TEMPLATES[form.action] || '';
    const nextDefault = DEFAULT_TEMPLATES[nextAction] || '';
    const responseEmptyOrDefault = !form.response.trim() || form.response.trim() === prevDefault.trim();
    setForm({
      ...form,
      action: nextAction,
      response: responseEmptyOrDefault ? nextDefault : form.response,
    });
  };

  const insertPlaceholder = (token: string) => {
    setForm({ ...form, response: `${form.response || ''}${token}` });
  };

  const formContent = (
    <form id="command-form" onSubmit={save}>
      <FormGrid>
        <FormRow>
          <Label>Trigger (sin !)</Label>
          <Input value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} required disabled={!!editingId} />
        </FormRow>
        <FormRow>
          <Label>Acción</Label>
          <Select value={form.action} onChange={(e) => onActionChange(e.target.value)}>
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </Select>
        </FormRow>
        <FormRow style={{ gridColumn: '1 / -1' }}>
          <Label>Descripción</Label>
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </FormRow>
        <FormRow style={{ gridColumn: '1 / -1' }}>
          <Label>Respuesta / plantilla (editable)</Label>
          <TextArea value={form.response} onChange={(e) => setForm({ ...form, response: e.target.value })} placeholder={responseHint} rows={4} />
          <Muted style={{ marginTop: '0.35rem' }}>{responseHint}</Muted>
          <div style={{ marginTop: '0.65rem', padding: '0.65rem 0.75rem', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Muted style={{ margin: '0 0 0.45rem', fontSize: '0.78rem', letterSpacing: '0.02em' }}>
              Glosario (click para insertar)
            </Muted>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: '0.28rem' }}>
              {glossaryKeys.map((key) => (
                <li key={key} style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', fontSize: '0.82rem' }}>
                  <button
                    type="button"
                    onClick={() => insertPlaceholder(key)}
                    style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                      fontSize: '0.78rem',
                      color: '#e8d5ff',
                      background: 'transparent',
                      border: '1px solid rgba(232,213,255,0.25)',
                      borderRadius: 4,
                      padding: '0.1rem 0.35rem',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                    title={`Insertar ${key}`}
                  >
                    {key}
                  </button>
                  <span style={{ color: '#9a8aa8' }}>= {PLACEHOLDER_GLOSSARY[key] || '—'}</span>
                </li>
              ))}
            </ul>
          </div>
        </FormRow>
        <FormRow>
          <Label>Cooldown (seg)</Label>
          <Input type="number" value={form.cooldownSeconds} onChange={(e) => setForm({ ...form, cooldownSeconds: parseInt(e.target.value, 10) || 0 })} />
        </FormRow>
        <FormRow style={{ display: 'flex', alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: '#b8a9c9', fontSize: '0.84rem' }}>
            <input type="checkbox" checked={form.adminOnly} onChange={(e) => setForm({ ...form, adminOnly: e.target.checked })} />
            Solo admin/mod
          </label>
        </FormRow>
      </FormGrid>
    </form>
  );

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Comandos</Title>
          <Subtitle>Gestioná respuestas del bot y mensajes automáticos por intervalo.</Subtitle>
        </PageHeaderMain>
        <PageActions>
          <TabBar>
            <TabBtn type="button" $active={tab === 'commands'} onClick={() => setTab('commands')}>Chat</TabBtn>
            <TabBtn type="button" $active={tab === 'timers'} onClick={() => setTab('timers')}>Timers</TabBtn>
          </TabBar>
          {tab === 'commands' && (
            <Button type="button" onClick={openCreate}>+ Nuevo comando</Button>
          )}
        </PageActions>
      </PageHeader>

      {toast && <Toast>{toast}</Toast>}

      {tab === 'timers' ? (
        <TimersPanel />
      ) : (
        <Surface>
          <SurfaceHeader>
            <Toolbar style={{ flex: 1 }}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar comando, acción o respuesta..."
                style={{ maxWidth: 420 }}
              />
              <Muted style={{ margin: 0 }}>{filtered.length}/{commands.length}</Muted>
            </Toolbar>
          </SurfaceHeader>
          <SurfaceBody $flush>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>Trigger</th>
                    <th style={{ width: '16%' }}>Acción</th>
                    <th>Respuesta</th>
                    <th style={{ width: '9%' }}>Estado</th>
                    <th style={{ width: '18%', textAlign: 'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c._id}>
                      <td>!{c.trigger}</td>
                      <td>{ACTION_LABELS[c.action] || c.action}</td>
                      <td style={{ maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.response || DEFAULT_TEMPLATES[c.action] || ''}>
                        {c.response || DEFAULT_TEMPLATES[c.action] || '(default)'}
                      </td>
                      <td><Badge $active={c.active}>{c.active ? 'Activo' : 'Off'}</Badge></td>
                      <td style={{ textAlign: 'right' }}>
                        <ActionBar>
                          <Button $variant="ghost" $size="sm" type="button" onClick={() => openEdit(c)}>Editar</Button>
                          <Button $variant="ghost" $size="sm" type="button" onClick={() => toggle(c._id)}>{c.active ? 'Pausar' : 'Activar'}</Button>
                          <Button $variant="danger" $size="sm" type="button" onClick={() => remove(c._id)}>×</Button>
                        </ActionBar>
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1.5rem' }}>Sin resultados</td></tr>
                  )}
                </tbody>
              </Table>
            </TableWrap>
          </SurfaceBody>
        </Surface>
      )}

      <Modal
        open={modalOpen}
        title={editingId ? 'Editar comando' : 'Nuevo comando'}
        onClose={closeModal}
        wide
        footer={(
          <>
            <Button $variant="ghost" type="button" onClick={closeModal}>Cancelar</Button>
            <Button type="submit" form="command-form">{editingId ? 'Guardar' : 'Crear'}</Button>
          </>
        )}
      >
        {formContent}
      </Modal>
    </Page>
  );
};

export default CommandsPage;
